import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { customersAPI, invoicesAPI, communicationsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Phone, Mail, MapPin, Calendar, DollarSign, 
  FileText, MessageSquare, Clock, Pencil
} from 'lucide-react';

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [communications, setCommunications] = useState([]);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [customerRes, invoicesRes, commsRes] = await Promise.all([
        customersAPI.getOne(id),
        invoicesAPI.getAll({ customer_id: id }),
        communicationsAPI.getAll({ customer_id: id })
      ]);
      
      setCustomer(customerRes.data);
      setInvoices(invoicesRes.data);
      setCommunications(commsRes.data);
    } catch (error) {
      toast.error('Failed to load customer details');
      navigate('/admin/customers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-64 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/customers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">{customer.contact_name}</h1>
          <p className="text-gray-500">Customer since {new Date(customer.created_at).toLocaleDateString()}</p>
        </div>
        <Badge variant={customer.status === 'active' ? 'default' : 'secondary'} className={customer.status === 'active' ? 'bg-lime-500' : ''}>
          {customer.status}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Card */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{customer.phone}</p>
                  </div>
                </div>
                {customer.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium">{customer.email}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Service Address</p>
                    <p className="font-medium">{customer.physical_address}</p>
                  </div>
                </div>
                {customer.billing_address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">Billing Address</p>
                      <p className="font-medium">{customer.billing_address}</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Service Details */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Service Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Frequency</p>
                  <p className="font-medium capitalize">{customer.service_frequency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Billing Plan</p>
                  <p className="font-medium capitalize">{customer.billing_plan}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Cost per Service</p>
                  <p className="font-medium">${customer.cost_per_service?.toFixed(2)}</p>
                </div>
                {customer.monthly_rate && (
                  <div>
                    <p className="text-sm text-gray-500">Monthly Rate</p>
                    <p className="font-medium">${customer.monthly_rate?.toFixed(2)}</p>
                  </div>
                )}
              </div>
              
              {customer.preferred_days?.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Preferred Days</p>
                  <div className="flex flex-wrap gap-2">
                    {customer.preferred_days.map(day => (
                      <Badge key={day} variant="outline">{day}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {customer.height_preference && (
                <div>
                  <p className="text-sm text-gray-500">Height Preference</p>
                  <p className="font-medium">{customer.height_preference}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          {(customer.work_notes || customer.preference_notes) && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {customer.work_notes && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Work Site Notes</p>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{customer.work_notes}</p>
                  </div>
                )}
                {customer.preference_notes && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Customer Preferences</p>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded">{customer.preference_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Recent Invoices */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Invoices</CardTitle>
              <Link to="/admin/invoices">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-gray-500 text-sm">No invoices yet</p>
              ) : (
                <div className="space-y-3">
                  {invoices.slice(0, 5).map(inv => (
                    <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-sm">{inv.invoice_number}</p>
                        <p className="text-xs text-gray-500">{new Date(inv.due_date).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lime-600">${inv.amount.toFixed(2)}</p>
                        <span className={`text-xs ${inv.status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                          {inv.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Communications */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <Link to="/admin/communications">
                <Button variant="ghost" size="sm">View All</Button>
              </Link>
            </CardHeader>
            <CardContent>
              {communications.length === 0 ? (
                <p className="text-gray-500 text-sm">No communication logs</p>
              ) : (
                <div className="space-y-3">
                  {communications.slice(0, 5).map(comm => (
                    <div key={comm.id} className="p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500 capitalize">{comm.contact_type}</span>
                        <span className="text-xs text-gray-400">• {comm.direction}</span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">{comm.notes}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(comm.created_at).toLocaleDateString()} by {comm.logged_by}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
